// =================================================================================================
// >>> **THE AKASHIC RECORDS ROOM — HER REDESIGN VERDICT, 2026-09-02.** <<<
//
// Her ruling on the live room: *the background is approved and stays; everything laid ON it is
// rejected as rushed.* Classy, clean, aligned, spacious — and *"functional but plain is a failure."*
// What changed, and why each was wrong before:
//
//   · **THE PANELS WERE OPAQUE BLACK SLABS.** They are the house GLASS now — `--room-panel` and the
//     house blur, which is N10-4's own ruling ("the glass panels were hiding the room art; TUNE, DO
//     NOT REMOVE") — so her art breathes through instead of being covered by a box.
//   · **THE HEADER CARRIED A YELLOW EYEBROW AND THE WRONG WORDS.** The eyebrow is gone and the crown
//     reads AKASHIC RECORDS ROOM. Her new art carries no title of its own, so per her art law this
//     header STANDS as the room's crown rather than yielding to the picture.
//   · **THE FIVE READINGS WERE CRUNCHED, AND THE CRUNCH WAS MINE.** I had capped the menu at 46rem,
//     so five cards divided nine rem each. The grid composes across the width now and stacks with
//     air on a phone.
//   · **THE READING WAS NEVER DRAWN AT ALL.** The room said "it is in your room" — a reading she
//     waited a minute for, delivered as a sentence about filing. It renders through
//     `NarrativeReportView`, the shell every narrative reading on this site already uses.
//
// The room is HANDED the real `SubjectPicker` rather than importing it: that component lives inside
// App.tsx and reaches five things there, and moving this room INTO App.tsx put the file over R8's
// ceiling, which roadmap 7 ratchets down and never up.
// =================================================================================================
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { ArrowLeft, X } from "lucide-react";
import { ReadingMenuSection } from "./ReadingMenuSection";
import { NarrativeReportView, type NarrativeReportData } from "./NarrativeReportView";
import { AKASHIC_READINGS, AKASHIC_DOOR_LABEL, akashicNeedsTwo } from "../akashic.js";
import { PersonAddModal } from "./PersonAddModal";

type PickMeta = { kind: "me" | "person" | "new"; personId?: string };
type Row = { key: string; label: string; blurb?: string; twoSubject?: boolean };

type Props = {
  onRequireMembership: (action: () => void) => void;
  goHome: () => void;
  backLabel: string;
  onAskLala?: (report: NarrativeReportData) => void;
  renderPicker: (opts: {
    label?: string;
    allowNew?: boolean;
    onPick: (chart: Record<string, unknown>, meta: PickMeta) => void;
  }) => ReactNode;
};

export function AkashicPage({ onRequireMembership, goHome, backLabel, onAskLala, renderPicker }: Props) {
  const [active, setActive] = useState<Row | null>(null);
  // >>> TWO SEATS, TWO FIELDS. <<< One shared `personId` was the shape that let the server honour the
  // picked person for one reading and ignore it for the other four (her accuracy defect, 2026-09-02).
  const [subjectId, setSubjectId] = useState("");
  const [otherId, setOtherId] = useState("");
  // IT #212 — the add-to-circle door and the tick that makes the picker re-read her circle after it.
  const [addOpen, setAddOpen] = useState(false);
  const [circleTick, setCircleTick] = useState(0);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [report, setReport] = useState<NarrativeReportData | null>(null);

  // **A TAP MUST ANSWER ITSELF.** Her report was *"nothing happens when you click on it"*: the panel
  // opened below the menu, off the bottom of the screen, which is indistinguishable from a dead
  // button. It is brought into view and focused on every pick.
  const flowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!active || !flowRef.current) return;
    flowRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    flowRef.current.focus({ preventScroll: true });
  }, [active]);

  const rows: Row[] = AKASHIC_READINGS.map((r) => ({
    key: r.key, label: r.label, blurb: r.blurb, twoSubject: akashicNeedsTwo(r.key),
  }));

  function open(row: Row) {
    // Both seats reset with the reading: a subject left over from the last one is how a member reads
    // somebody she did not choose.
    setActive(row); setStatus(""); setReport(null); setSubjectId(""); setOtherId("");
  }

  function close() { setActive(null); setReport(null); setStatus(""); }

  async function read(row: Row) {
    if (row.twoSubject && !otherId) {
      setStatus("Choose the person from your circle you want to be read against.");
      return;
    }
    setBusy(true); setStatus(""); setReport(null);
    try {
      const res = await fetch("/api/akashic/reading", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reading: row.key,
          subjectId: subjectId || null,
          otherId: row.twoSubject ? otherId : null,
        }),
      });
      if (res.status === 401) { setStatus("Sign in first."); return; }
      const data = await res.json().catch(() => null);
      if (!data?.ok) { setStatus(data?.message || "That reading could not be finished right now."); return; }
      if (data.report) setReport(data.report as NarrativeReportData);
      else setStatus("It is in your room.");
    } catch {
      setStatus("That reading could not be finished right now.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="page-shell specialty-page akashic-page">
      <div className="akashic-backdrop" aria-hidden="true" />
      <button className="back-button" type="button" onClick={goHome}>
        <ArrowLeft size={18} />
        {backLabel}
      </button>

      {/* THE CROWN. No eyebrow above it — her word — and the room's own name in the house gold. */}
      <div className="specialty-hero akashic-hero">
        <h1 className="akashic-title">Akashic Records Room</h1>
        <p>
          Who you have been, what you carry, the agreements you came in with, what you already
          mastered, and when the turning points arrive.
        </p>
      </div>

      <ReadingMenuSection
        rows={rows}
        className="akashic-menu"
        ariaLabel="The five readings"
        lead="Choose a reading."
        tag="Two people"
        showBlurb
        onPick={open}
      />

      {active ? (
        <div className="akashic-flow" ref={flowRef} tabIndex={-1}>
          <div className="akashic-flow__head">
            <h2>{active.label}</h2>
            <button type="button" className="akashic-flow__close" onClick={close} aria-label="Close this reading">
              <X size={18} />
            </button>
          </div>

          {report ? (
            <>
              {/* THE STANDING REPORT PANEL — the shell every narrative reading on this site uses. */}
              <NarrativeReportView data={report} />
              {/* Her door, composed at the foot rather than shouting. */}
              <div className="akashic-flow__door">
                <button type="button" className="button button--ghost" onClick={() => onAskLala?.(report)}>
                  {AKASHIC_DOOR_LABEL}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* THE SUBJECT PICKER LAW: every generator needing birth details opens with it. */}
              {renderPicker({
                label: active.twoSubject ? "WHO IS THE FIRST PERSON?" : undefined,
                onPick: (_c, m) => { setSubjectId(m.kind === "person" ? (m.personId || "") : ""); },
              })}

              {active.twoSubject ? (
                <>
                  {/* >>> **IT #212 — THE SECOND SEAT ASKED FOR HER CIRCLE AND GAVE HER NO WAY TO
                      FILL IT.** <<< `allowNew` stays FALSE here and that is correct: this reading
                      needs a person ID for the second subject, and a fresh unsaved subject has
                      none. What was missing was the other half — a door to ADD somebody. It is
                      keyed on `circleTick` so the picker re-reads her circle the moment one is
                      saved, rather than leaving her looking at the list she just changed. */}
                  <div key={circleTick}>
                    {renderPicker({
                      label: "AND WHO ARE THEY?",
                      allowNew: false,
                      onPick: (_c, m) => { setOtherId(m.kind === "person" ? (m.personId || "") : ""); },
                    })}
                  </div>
                  {!otherId ? (
                    <p className="akashic-flow__hint">
                      Pick someone from your circle for the second seat, or add them.
                    </p>
                  ) : null}
                  <button type="button" className="button button--ghost akashic-flow__add"
                    onClick={() => setAddOpen(true)}>
                    Add someone to my circle
                  </button>
                </>
              ) : null}

              <button
                className={`button button--primary akashic-flow__go${busy ? " is-generating" : ""}`}
                type="button"
                disabled={busy}
                onClick={() => onRequireMembership(() => { void read(active); })}
              >
                {busy ? "Reading your record…" : `Read ${active.label}`}
              </button>

              {status ? <p className="akashic-flow__status" role="status">{status}</p> : null}
            </>
          )}
        </div>
      ) : null}
      {addOpen ? (
        <PersonAddModal
          onClose={() => setAddOpen(false)}
          onSaved={() => { setAddOpen(false); setCircleTick((n) => n + 1); }}
        />
      ) : null}
    </section>
  );
}
